import { useId, useState, type KeyboardEvent } from 'react'
import { ErrorDeApi } from '../servicios/api'
import { useAyuda } from './ContextoDeAyuda'
import { TEMAS_DEL_RECORRIDO, TEXTOS_DEL_MENU_DE_AYUDA, type TemaDelRecorrido } from './contenidoDelRecorrido'
import './recorrido.css'

/**
 * Menú «Ayuda», junto a la campana (RF-1702): relanza el recorrido completo o por tema, y enciende o apaga los
 * TIPs (RF-1707). Sigue el patrón de la campana: un botón que abre y cierra un panel; además, Esc lo cierra.
 */
export function MenuDeAyuda() {
  const { estado, mostrarTips, cambiarMostrarTips, lanzarRecorrido } = useAyuda()
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const idPanel = useId()
  const idExplicacion = useId()

  function elegir(tema: TemaDelRecorrido | null) {
    setAbierto(false)
    lanzarRecorrido(tema ?? undefined)
  }

  async function alternarTips(mostrar: boolean) {
    setGuardando(true)
    setError('')
    try {
      await cambiarMostrarTips(mostrar)
    } catch (e) {
      setError(e instanceof ErrorDeApi && e.message ? e.message : TEXTOS_DEL_MENU_DE_AYUDA.errorAlGuardar)
    } finally {
      setGuardando(false)
    }
  }

  function alTeclado(evento: KeyboardEvent) {
    if (evento.key === 'Escape' && abierto) {
      evento.stopPropagation()
      setAbierto(false)
    }
  }

  return (
    <span className="menu-ayuda" data-recorrido="menu-ayuda" onKeyDown={alTeclado}>
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-haspopup="true"
        aria-expanded={abierto}
        aria-controls={abierto ? idPanel : undefined}
      >
        {TEXTOS_DEL_MENU_DE_AYUDA.boton}
      </button>

      {abierto && (
        <div id={idPanel} className="menu-ayuda-panel" role="group" aria-label={TEXTOS_DEL_MENU_DE_AYUDA.tituloDelMenu}>
          <p className="kicker">{TEXTOS_DEL_MENU_DE_AYUDA.seccionRecorrido}</p>
          <div className="menu-ayuda-opciones">
            <button type="button" className="enlace" onClick={() => elegir(null)}>
              {TEXTOS_DEL_MENU_DE_AYUDA.recorridoCompleto}
            </button>
            {TEMAS_DEL_RECORRIDO.map((t) => (
              <button key={t.clave} type="button" className="enlace" onClick={() => elegir(t.clave)}>
                {t.titulo}
              </button>
            ))}
          </div>

          <label className="menu-ayuda-interruptor">
            <input
              type="checkbox"
              role="switch"
              checked={mostrarTips}
              disabled={!estado || guardando}
              aria-describedby={idExplicacion}
              onChange={(e) => void alternarTips(e.target.checked)}
            />
            {TEXTOS_DEL_MENU_DE_AYUDA.mostrarTips}
          </label>
          <p id={idExplicacion} className="sutil" style={{ margin: 0 }}>
            {estado ? TEXTOS_DEL_MENU_DE_AYUDA.explicacionDeTips : TEXTOS_DEL_MENU_DE_AYUDA.sinPreferencias}
          </p>
          {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        </div>
      )}
    </span>
  )
}
